import { Directive, input, signal } from '@angular/core';
import { BrnButton } from '@spartan-ng/brain/button';
import { classes } from '@lite-workspace/shared/helm-ui/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'clsx';
import { injectBrnButtonConfig } from './hlm-button-token';

export const buttonVariants = cva(
  [
    'group/button inline-flex shrink-0 items-center justify-center whitespace-nowrap cursor-pointer rounded-lg',
    'text-control font-semibold outline-none select-none',
    // Переход только по краске. `transition-all` анимировал и геометрию — при смене варианта
    // (outline → default) вместе с цветом плыли ширина рамки и размеры, и нажатие читалось как мигание.
    'transition-[var(--t-prop-paint)] duration-[var(--t-fast)] ease-[var(--ease-smooth)]',
    'focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none',
    'data-disabled:pointer-events-none data-disabled:opacity-50',
    '[&_ng-icon]:pointer-events-none [&_ng-icon]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:opacity-90',
        secondary: 'bg-panel text-ink hover:bg-panel2',
        // Чернильная заливка главного действия мастера: цвет текста и фон меняются местами вместе с темой.
        ink: 'bg-ink font-medium text-panel hover:opacity-90',
        outline: 'border border-line2 bg-panel text-c2 hover:border-acc-line hover:bg-panel2 hover:text-ink',
        ghost: 'text-c2 hover:bg-panel2 hover:text-ink',
        // Второстепенная кнопка на плите подсказки (коуч-карточка, гид админки): краска плиты, а не панели.
        coach: 'text-coach-body hover:bg-coach-wash hover:text-coach-ink',
        // Акцентная рамка на светлой заливке акцента: главное действие панели, когда обычный outline теряется среди соседей.
        accent: 'border border-acc bg-acc/10 text-ink hover:bg-acc/20',
        // Тихая акцентная ссылка рядом с главной кнопкой (поддержка в окне беты): контур и подпись акцента на мягкой заливке.
        'acc-soft': 'border border-acc-line bg-acc-soft font-medium text-acc2 hover:bg-acc/20',
        // Act of a text row (fix beside dismiss): accent hairline and label, no fill — a filled look is too heavy in prose.
        'acc-outline': 'border border-acc bg-transparent text-acc hover:bg-acc-softer',
        blocked: [
          'border border-[var(--acc-line)] bg-[var(--acc-muted)]',
          'text-[color:var(--acc-muted-ink)] hover:bg-[var(--acc-muted-2)] data-disabled:opacity-100',
        ].join(' '),
        destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
        // Подпись берёт общий цвет заливочных вариантов (`--on-acc`), а не `--on-ok`: значения совпадают в обеих темах.
        success: 'bg-ok text-on-acc hover:opacity-90',
        link: 'text-acc underline-offset-4 hover:underline',
        // Тихая текстовая ссылка без плашки и подчёркивания: второстепенное действие рядом с основным текстом.
        quiet: 'text-c3 hover:text-ink',
        // Контурная кнопка на тёмной плите меню (трей отбора, тост-предупреждение): рамка и подпись — тоном плиты.
        'on-menu-outline':
          'border border-on-menu-surface/35 text-on-menu-surface/75 hover:bg-on-menu-surface/10 hover:text-on-menu-surface',
        // Круглая иконка-кнопка поверх фото/медиа: плотная плашка-скрим, одинаковая в обеих темах.
        // Hover-reveal (opacity-0 group-hover:opacity-100) остаётся у потребителя — это поведение контекста, не кнопки.
        'on-media-icon':
          'rounded-full bg-[var(--scrim-ctrl-2)] text-on-media backdrop-blur-sm transition-opacity duration-[var(--t-fast)]',
        // Тот же круг-скрим с белой волосяной рамкой — держится и на светлом кадре. `data-state="on"` — нажатая кнопка:
        // белая плашка с тёмной иконкой и тонким кольцом скрима вокруг (действия над роликом в библиотеке уроков).
        'on-media-ring': [
          'rounded-full bg-[var(--scrim-ctrl-2)] text-on-media backdrop-blur-xs',
          'shadow-[inset_0_0_0_var(--border-width-hairline)_color-mix(in_oklch,var(--on-media)_30%,transparent)]',
          'hover:bg-[var(--plate-solid)]',
          'hover:shadow-[inset_0_0_0_var(--border-width-hairline)_color-mix(in_oklch,var(--on-media)_55%,transparent)]',
          'data-[state=on]:bg-on-media data-[state=on]:text-stage-black data-[state=on]:shadow-[var(--shadow-scrim-ring)]',
        ].join(' '),
        // Текстовая пилюля поверх фото/медиа: тот же скрим, что у `on-media-icon`, но с подписью и hover-уплотнением.
        'on-media-text': 'rounded-full bg-[var(--scrim-ctrl)] text-on-media backdrop-blur-sm hover:bg-[var(--scrim-ctrl-2)]',
      },
      size: {
        default: 'h-9 gap-2 px-4',
        xs: 'h-7 gap-1 px-2.5 text-xs',
        sm: 'h-8 gap-1.5 px-3',
        // The compact bar button of the editor/gallery headers.
        action: 'h-7.5 gap-1.5 rounded-md px-3 text-sm',
        // Пилюля главного действия в шапке приложения: та же плотность, что у action, но со скруглением до овала.
        pill: 'h-7.5 gap-1.5 rounded-full px-3.25 text-sm',
        lg: 'h-10 gap-2 px-5 text-sm',
        // Главное действие окна на всю ширину (48px): копирование ссылки, выбор способа отбора.
        xl: 'h-12 gap-2 px-6 text-sm',
        // Кнопка в одном ряду с полем настроек (`h-11`): форма как у lg, высота как у поля.
        field: 'h-11 gap-2 px-5 text-sm',
        // Крупная пилюля главного действия мастера.
        'pill-lg': 'h-11 gap-2 rounded-full px-6.5 text-catalog-body',
        // Пилюля с иконкой слева (выход из мастера): отступ у иконки меньше, чем у подписи.
        'pill-md': 'h-10 gap-2 rounded-full pr-4 pl-3 text-sm',
        // Small pill inside a text row (problem list act pair).
        'pill-xs': 'h-6.5 gap-1.5 rounded-full px-3.5',
        // Кнопка футера модального окна: выше и шире обычной, чтобы пара «Отменить/Продолжить» держала низ диалога.
        dialog: 'h-11.5 gap-2 px-6',
        icon: 'size-9',
        'icon-xs': 'size-7',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

@Directive({
  selector: 'button[hlmBtn], a[hlmBtn]',
  exportAs: 'hlmBtn',
  hostDirectives: [{ directive: BrnButton, inputs: ['disabled'] }],
  host: { 'data-slot': 'button' },
})
export class HlmButton {
  private readonly _config = injectBrnButtonConfig();
  private readonly _additionalClasses = signal<ClassValue>('');

  public readonly variant = input<ButtonVariants['variant']>(this._config.variant);
  public readonly size = input<ButtonVariants['size']>(this._config.size);

  constructor() {
    classes(() => [buttonVariants({ variant: this.variant(), size: this.size() }), this._additionalClasses()]);
  }

  setClass(cls: string): void {
    this._additionalClasses.set(cls);
  }
}
